import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Content,
  Header,
  Page,
  Progress,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  makeStyles,
  Box,
  Paper,
  Button,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import {
  GithubTeamsBar,
  GitRepoSection,
  PullRequestsSection,
  homepageLinksApiRef,
} from '@internal/plugin-homepage-links';
import type { GithubTeam } from '@internal/plugin-homepage-links';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
  },
  teamsBarWrapper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  contentSection: {
    marginBottom: theme.spacing(2),
  },
  editButtonContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

// Wrapper to safely use API
function useHomepageLinksApi() {
  try {
    return useApi(homepageLinksApiRef);
  } catch (e) {
    console.error('Failed to get homepage links API:', e);
    return null;
  }
}

export const GithubRepositories = () => {
  const classes = useStyles();
  const api = useHomepageLinksApi();

  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);

  // GitHub teams state for filtering - same as HomePage
  const [selectedTeamSlugs, setSelectedTeamSlugs] = useState<string[]>([]);
  const [_githubUsername, setGithubUsername] = useState<string | null>(null);
  const [userSelected, setUserSelected] = useState(true);

  // Track whether default selections have been applied
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // Convert selected team slugs to Backstage group refs for filtering
  const selectedGroupRefs = useMemo(() => {
    return selectedTeamSlugs.map(slug => `group:default/${slug}`);
  }, [selectedTeamSlugs]);

  // Handler for when teams are loaded from GitHub
  const handleTeamsLoaded = useCallback((teams: GithubTeam[]) => {
    // Only initialize with all teams if defaults haven't been applied yet
    if (!defaultsApplied) {
      setSelectedTeamSlugs(teams.map(t => t.slug));
    }
    setLoading(false);
  }, [defaultsApplied]);

  // Handler for team selection changes
  const handleTeamSelectionChange = useCallback((newSelectedTeams: string[]) => {
    setSelectedTeamSlugs(newSelectedTeams);
  }, []);

  // Handler for when username is loaded from GitHub
  const handleUsernameLoaded = useCallback((username: string) => {
    setGithubUsername(username);
  }, []);

  // Handler for user selection changes
  const handleUserSelectionChange = useCallback((selected: boolean) => {
    setUserSelected(selected);
  }, []);

  // Handler for saving default selections
  const handleSaveDefault = useCallback(async () => {
    if (!api) return;
    try {
      await api.updatePreferences({
        defaultSelectedTeams: selectedTeamSlugs,
        defaultUserSelected: userSelected,
      });
      setDefaultsApplied(true);
    } catch (err) {
      console.error('Failed to save default selections:', err);
    }
  }, [api, selectedTeamSlugs, userSelected]);

  // Load preferences including default selections
  const loadPreferences = useCallback(async () => {
    if (!api) {
      setLoading(false);
      return;
    }
    try {
      const prefs = await api.getPreferences();
      // Apply saved default selections if they exist
      if (prefs.defaultSelectedTeams !== undefined) {
        setSelectedTeamSlugs(prefs.defaultSelectedTeams);
        setDefaultsApplied(true);
      }
      if (prefs.defaultUserSelected !== undefined) {
        setUserSelected(prefs.defaultUserSelected);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  }, [api]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleEditModeToggle = useCallback(() => {
    setEditMode(prev => !prev);
  }, []);

  return (
    <Page themeId="tool">
      <Header
        title="GitHub Repositories"
        subtitle="View and manage your team's repositories"
      />
      <Content>
        {/* GitHub Teams Bar - same component as homepage */}
        <Paper className={classes.teamsBarWrapper}>
          <GithubTeamsBar
            selectedTeams={selectedTeamSlugs}
            userSelected={userSelected}
            onSelectionChange={handleTeamSelectionChange}
            onUserSelectionChange={handleUserSelectionChange}
            onTeamsLoaded={handleTeamsLoaded}
            onUsernameLoaded={handleUsernameLoaded}
            editMode={editMode}
            onSaveDefault={handleSaveDefault}
          />
          <Box className={classes.editButtonContainer}>
            <Button
              size="small"
              variant={editMode ? 'contained' : 'outlined'}
              color={editMode ? 'primary' : 'default'}
              startIcon={editMode ? <CheckIcon /> : <EditIcon />}
              onClick={handleEditModeToggle}
            >
              {editMode ? 'Done' : 'Edit repos'}
            </Button>
          </Box>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <Progress />
          </Box>
        ) : (
          <>
            {/* Pull Requests Section */}
            <Box className={classes.contentSection}>
              <PullRequestsSection maxPRs={15} editMode={editMode} />
            </Box>

            {/* Git Repositories Section - using shared component with expandable mode */}
            <Box className={classes.contentSection}>
              <GitRepoSection
                selectedGroupRefs={selectedGroupRefs}
                userSelected={userSelected}
                editMode={editMode}
                expandable={true}
                initialExpanded={true}
                maxRepos={10}
              />
            </Box>
          </>
        )}
      </Content>
    </Page>
  );
};
